// client/src/lib/ToastProvider.js
//
// App-level toast surface. Mount ABOVE the Router (and above
// AppChromeLayout) so any route — app-level, team-detail, kitchen-sink —
// can fire toasts through useToast() without a route-specific setup.
//
// Visual grammar: "Sticker-adjacent." Each toast is a slightly-tilted
// paper chip with a tone-matched left stripe (marker for success, red
// for error, accent for info). Stickers are decorative and permanent;
// toasts are informational and ephemeral, so they belong to the same
// family without being the same primitive.
//
// Behavior: auto-dismiss after ~4s. Tap to dismiss earlier. Stack sits
// above the team-detail tab bar (110px) so it doesn't collide with
// mobile-frame chrome on team routes.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const ToastContext = createContext(null);

const AUTO_DISMISS_MS = 4000;

// Tone → left-stripe color. Class names match the token keys in
// tailwind.config.js. If a token gets renamed, swap here — every toast
// routes through this single map, not through raw strings scattered
// across the file.
const STRIPE_CLASS = {
  success: 'bg-marker',  // purple — the mischief palette's success tone
  error:   'bg-red',     // red — urgent/error
  info:    'bg-ice',     // ice accent (CSS var) — informational
};

// A tiny counter is enough for uniqueness — toasts live for seconds,
// never at scale. Not worth reaching for crypto.randomUUID().
let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Track outstanding timeouts by id so tap-to-dismiss can cancel the
  // auto-timer (otherwise a dismissed toast tries to dismiss itself
  // again 4s later — harmless but noisy in dev logs).
  const timeoutsRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (kind, message) => {
      const id = nextId++;
      // Felt-tilt: small random rotation baked in at creation, same
      // pattern the mischief primitives use — set once, not per render,
      // so the toast doesn't jitter if it re-renders while visible.
      // Range ±1° to ±4°, skipping the flat middle for that hand-placed
      // feel.
      const rotate = (Math.random() * 3 + 1) * (Math.random() < 0.5 ? -1 : 1);
      setToasts((prev) => [...prev, { id, kind, message, rotate }]);

      const timeout = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      timeoutsRef.current.set(id, timeout);
    },
    [dismiss]
  );

  // Public imperative API. Matches how forms will call it:
  //   const toast = useToast();
  //   toast.success('Announcement posted');
  const api = useMemo(
    () => ({
      success: (message) => push('success', message),
      error:   (message) => push('error', message),
      info:    (message) => push('info', message),
    }),
    [push]
  );

  // Cleanup all pending timeouts on unmount (StrictMode double-mount in
  // dev, HMR reloads, etc.). Belt-and-suspenders.
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
      timeouts.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// The visible stack. Fixed to the bottom of the viewport, above the
// team-detail tab bar (110px) plus 16px breathing room. Newest toasts
// stack at the bottom (closest to fingers on mobile). pointer-events
// is off on the container so the empty space around the toasts stays
// tap-through; individual toasts re-enable it.
function ToastStack({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed left-0 right-0 bottom-[126px] z-50 flex flex-col items-center gap-2 px-4 pointer-events-none"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  const stripeClass = STRIPE_CLASS[toast.kind] ?? 'bg-ink';
  return (
    <button
      type="button"
      onClick={onDismiss}
      style={{ transform: `rotate(${toast.rotate}deg)` }}
      className={[
        'pointer-events-auto',
        'w-full max-w-sm',
        'flex items-stretch',
        'bg-paper text-ink',
        'border border-rule rounded-md',
        'shadow-md',
        'text-left',
      ].join(' ')}
    >
      {/* Left stripe — the tone signal */}
      <span
        className={`w-1.5 shrink-0 rounded-l-md ${stripeClass}`}
        aria-hidden="true"
      />
      <span className="px-4 py-3 text-sm font-medium">{toast.message}</span>
    </button>
  );
}

// Hook for consumers.
//   const toast = useToast();
//   toast.success('Saved');
//   toast.error('Something went wrong');
//   toast.info('Draft restored');
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}